import Icon from '../../../../images/icons/name.svg';
import { RegisterWidget, Widget } from '../../../lib/widget';
import template from './pumptest.html';
import { type Tag, TagAttribute, type TagDefinition } from '../../../lib/tag';
import { Role } from '../../../../role';
import FrameMaker from '../../../../framemaker';
import LiveData from '../../../../livedata';
import FrameParser from '../../../../frameparser';
import { Checkbox } from '../../../input/checkbox/checkbox';
import { TagToggleSwitch } from '../../../input/toggle/toggleswitch';
import { Carousel } from '../../../layout/carousel/carousel';
import { convert, TagUnit } from '../../../lib/tagunits';
import { RadioButton, TagRadioButton } from '../../../input/radio/radiobutton';
import { PushButton, TagPushButton } from '../../../input/pushbutton/pushbutton';
import { Accordion } from '../../../layout/accordion/accordion';
import ArrowIcon from '../../../../images/icons/arrow_right_filled.svg';

const TestState = {
    READY_TO_TEST: 0,
    PRE_TEST: 1,
    STARTING_TEST: 2,
    TESTING: 3,
    POST_TEST: 4
} as const;

type TestStates = typeof TestState[keyof typeof TestState];

@RegisterWidget({ tag: 'tc-pump-test', template: template, displayName: 'Pump Test', icon: Icon, section: 'Pumps' })
export class PumpTest extends Widget {
    private startSection: HTMLElement;
    private startAccordion: Accordion;
    private startButton: TagPushButton;
    private preTestSection: HTMLElement;
    private preTestAccordion: Accordion;
    private postTestSection: HTMLElement;
    private postTestAccordion: Accordion;
    private testControlsSection: HTMLElement;
    private testControlsAccordion: Accordion;
    private testMessage: HTMLElement;
    private testStatus: HTMLElement;
    private preCheck: HTMLElement;
    private postCheck: HTMLElement;
    private manTestRadio: TagRadioButton;
    private manCollectButton: TagPushButton;
    private manEndTestButton: TagPushButton;
    private abortButton: TagPushButton;
    private prechecks: Checkbox[] = [];
    private postchecks: Checkbox[] = [];

    private curveTimestampTag: Tag;
    private pumpTestTag: Tag;
    private pumpStartControlTag: Tag;
    private pumpRunningTag: Tag;
    private pumpSpeedControlTag: Tag;
    private pumpHasSpeedControlTag: Tag;
    private pumpSystemStateTag: Tag;
    private pumpActSpeedTag: Tag;
    private minBepRatioTag: Tag;
    private maxBepRatioTag: Tag;

    private manualTestTag: Tag;
    private manualCollectTag: Tag;
    private manualEndTestTag: Tag;
    private testStartTimeTag: Tag;
    private testStepTimeTag: Tag;
    private testFinTimeTag: Tag;
    private testStateTag: Tag;
    private preCheckDoneTag: Tag;
    private postCheckDoneTag: Tag;
    private systemStateTag: Tag;

    private testSpeed: number;

    // Data
    private testFlows: number[] = [];
    private testHeads: number[] = [];
    private maxSpeed: number;

    // Conversion Factors
    private flowConversion: number;
    private headConversion: number;

    @TagAttribute({
        displayName: 'DPO Folder',
        supportedRoles: [Role.ROLE_DPO_FOLDER]
    }) dpoFolder: TagDefinition;

    @TagAttribute({
        displayName: 'Pump Tag',
        supportedRoles: [Role.ROLE_PUMP]
    }) pumpTag: TagDefinition;

    @TagAttribute({
        displayName: 'Model Pump Tag',
        supportedRoles: [Role.ROLE_MODEL_PUMP]
    }) modelPumpTag: TagDefinition;

    //@TagAttribute({displayName: 'Manual Test Tag',      shouldSubscribe: true, supportedRoles: [Role.ROLE_TLC_MANUAL_TEST_MODE]}) manualTestTag: TagDefinition;
    //@TagAttribute({displayName: 'Manual Collect Tag',   shouldSubscribe: true, supportedRoles: [Role.ROLE_TLC_MANUAL_TEST_COLLECT_DATA]}) manualCollectTag: TagDefinition;
    //@TagAttribute({displayName: 'Manual End Test Tag',  shouldSubscribe: true, supportedRoles: [Role.ROLE_TLC_MANUAL_TEST_END_TEST]}) manualEndTestTag: TagDefinition;
    //@TagAttribute({displayName: 'Test Start Time Tag',  shouldSubscribe: true, supportedRoles: [Role.ROLE_TLC_TEST_START_TIME]}) testStartTimeTag: TagDefinition;
    //@TagAttribute({displayName: 'Test Step Time Tag',   shouldSubscribe: true, supportedRoles: [Role.ROLE_TLC_TEST_STEP_TIME]}) testStepTimeTag: TagDefinition;
    //@TagAttribute({displayName: 'Test Final Time Tag',  shouldSubscribe: true, supportedRoles: [Role.ROLE_TLC_TEST_COMPLETE_TIME]}) testFinTimeTag: TagDefinition;
    //@TagAttribute({displayName: 'Test State Tag',       shouldSubscribe: true, supportedRoles: [Role.ROLE_TLC_TEST_STATE]}) testStateTag: TagDefinition;
    //@TagAttribute({displayName: 'Pre Check Done Tag',   shouldSubscribe: true, supportedRoles: [Role.ROLE_TLC_PRE_CHECKLIST_DONE]}) preCheckDoneTag: TagDefinition;
    //@TagAttribute({displayName: 'Post Check Done Tag',  shouldSubscribe: true, supportedRoles: [Role.ROLE_TLC_POST_CHECKLIST_DONE]}) postCheckDoneTag: TagDefinition;

    protected connectedCallback(): void {
        this.startSection = this.shadowRoot?.getElementById('start-test') as HTMLElement;
        this.startAccordion = this.shadowRoot?.getElementById('start-accordion') as Accordion;
        this.preTestSection = this.shadowRoot?.getElementById('pre-test-checklist') as HTMLElement;
        this.preTestAccordion = this.shadowRoot?.getElementById('pre-test-accordion') as Accordion;
        this.postTestSection = this.shadowRoot?.getElementById('post-test-checklist') as HTMLElement;
        this.postTestAccordion = this.shadowRoot?.getElementById('post-test-accordion') as Accordion;
        this.testControlsSection = this.shadowRoot?.getElementById('test-controls') as HTMLElement;
        this.testControlsAccordion = this.shadowRoot?.getElementById('test-controls-accordion') as Accordion;
        this.preCheck = this.shadowRoot?.getElementById('pre-check') as HTMLElement;
        this.postCheck = this.shadowRoot?.getElementById('post-check') as HTMLElement;
        this.testMessage = this.shadowRoot?.getElementById('state') as HTMLElement;
        this.testStatus = this.shadowRoot?.getElementById('test-status') as HTMLElement;
        this.manTestRadio = this.shadowRoot?.getElementById('mode-select') as TagRadioButton;
        this.manCollectButton = this.shadowRoot?.getElementById('collect') as TagPushButton;
        this.manEndTestButton = this.shadowRoot?.getElementById('end-test') as TagPushButton;
        this.abortButton = this.shadowRoot?.getElementById('abort') as TagPushButton;
        this.startButton = this.shadowRoot?.getElementById('start-button') as TagPushButton;

        let startImage = this.shadowRoot?.getElementById('start-test-image') as HTMLImageElement;
        startImage.src = ArrowIcon;

        let preTestImage = this.shadowRoot?.getElementById('pre-test-checklist-image') as HTMLImageElement;
        preTestImage.src = ArrowIcon;

        let postTestImage = this.shadowRoot?.getElementById('post-test-checklist-image') as HTMLImageElement;
        postTestImage.src = ArrowIcon;

        let testControlsImage = this.shadowRoot?.getElementById('test-controls-image') as HTMLImageElement;
        testControlsImage.src = ArrowIcon;

        this.startSection.onclick = () => {
            this.startAccordion.toggle();
            this.toggleArrow(startImage, this.startAccordion);
        };
        this.preTestSection.onclick = () => {
            this.preTestAccordion.toggle();
            this.toggleArrow(preTestImage, this.preTestAccordion);
        };
        this.postTestSection.onclick = () => {
            this.postTestAccordion.toggle();
            this.toggleArrow(postTestImage, this.postTestAccordion);
        };
        this.testControlsSection.onclick = () => {
            this.testControlsAccordion.toggle();
            this.toggleArrow(testControlsImage, this.testControlsAccordion);
        };
    }

    protected enliven(): void {
        let dpoFolder = this.dpoFolder.tag;
        let pumpFolder = this.pumpTag.tag;
        let modelPumpFolder = this.modelPumpTag.tag;

        this.flowConversion = convert(1, TagUnit.TU_GPM, pumpFolder.parent.findChildByRole(Role.ROLE_TOTAL_FLOW)!.units);
        this.headConversion = convert(1, TagUnit.TU_FEET_HEAD, pumpFolder.parent.findChildByRole(Role.ROLE_PUMP_HEAD)!.units);
        this.maxSpeed = pumpFolder.parent.findChildByRole(Role.ROLE_MAX_SPEED_HZ)!.getValue();

        // Subscribe to all the necessary tags
        this.manualTestTag = dpoFolder.findChildByRole(Role.ROLE_TLC_MANUAL_TEST_MODE)!;
        this.manualCollectTag = dpoFolder.findChildByRole(Role.ROLE_TLC_MANUAL_TEST_COLLECT_DATA)!;
        this.manualEndTestTag = dpoFolder.findChildByRole(Role.ROLE_TLC_MANUAL_TEST_END_TEST)!;
        this.testStartTimeTag = dpoFolder.findChildByRole(Role.ROLE_TLC_TEST_START_TIME)!;
        this.testStepTimeTag = dpoFolder.findChildByRole(Role.ROLE_TLC_TEST_STEP_TIME)!;
        this.testFinTimeTag = dpoFolder.findChildByRole(Role.ROLE_TLC_TEST_COMPLETE_TIME)!;
        this.testStateTag = dpoFolder.findChildByRole(Role.ROLE_TLC_TEST_STATE)!;
        this.preCheckDoneTag = dpoFolder.findChildByRole(Role.ROLE_TLC_PRE_CHECKLIST_DONE)!;
        this.postCheckDoneTag = dpoFolder.findChildByRole(Role.ROLE_TLC_POST_CHECKLIST_DONE)!;
        this.systemStateTag = pumpFolder.parent.findChildByRole(Role.ROLE_SYSTEM_CURVE_STATE)!;
        this.minBepRatioTag = pumpFolder.findChildByRole(Role.ROLE_PUMP_MIN_BEP_RATIO)!;
        this.maxBepRatioTag = pumpFolder.findChildByRole(Role.ROLE_PUMP_MAX_BEP_RATIO)!;


        this.manCollectButton.buttonTag = {tag: this.manualCollectTag};
        this.manEndTestButton.buttonTag = {tag: this.manualEndTestTag};

        this.curveTimestampTag = pumpFolder.findChild('CurveTimestamp')!;

        this.preCheckDoneTag = dpoFolder.findChildByRole(Role.ROLE_TLC_PRE_CHECKLIST_DONE)!;
        this.postCheckDoneTag = dpoFolder.findChildByRole(Role.ROLE_TLC_POST_CHECKLIST_DONE)!;
        this.pumpHasSpeedControlTag = pumpFolder.findChildByRole(Role.ROLE_PUMP_SPEED_CONTROLLED)!;
        this.testStateTag = dpoFolder.findChildByRole(Role.ROLE_TLC_TEST_STATE)!;

        this.pumpSystemStateTag = dpoFolder.findChildByRole(Role.ROLE_SYSTEM_CURVE_STATE)!;
        this.pumpTestTag = pumpFolder.findChildByRole(Role.ROLE_PUMP_TEST)!;

        this.startButton.buttonTag = { tag: this.pumpTestTag };
        this.manTestRadio.valueTag = { tag: this.manualTestTag };
        this.abortButton.buttonTag = { tag: this.pumpTestTag };

        this.abortButton.onchange = () => this.pumpTestTag.setPendingValue(false, this);

        this.pumpStartControlTag = pumpFolder.findChildByRole(Role.ROLE_PUMP_START_CONTROLLED)!;
        this.pumpRunningTag = pumpFolder.findChildByRole(Role.ROLE_BOOL_RUNNING)!;
        this.pumpSpeedControlTag = pumpFolder.findChildByRole(Role.ROLE_AUTO_SPEED_CONTROLLABLE)!;

        let listState = document.createElement('tag-toggle-switch') as TagToggleSwitch;
        listState.toggleTag = { tag: this.preCheckDoneTag };
        import('../../../../livedataclient').then(ldc => {
            let fm = new FrameMaker();
            fm.buildFrame(LiveData.LDC_TEST_CHECKLISTS, dpoFolder.device.id);
            ldc.default.sendRequest(fm).then(fp => this.onTestChecklists(fp))
        });
    }

    onTestChecklists(fp: FrameParser) {
        this.shadowRoot!.querySelectorAll('.loader').forEach(loader => {
            if (loader instanceof HTMLElement)
                loader.style.display = 'none';
        })
        this.createChecklistRow(fp, this.preCheck, this.preCheckDoneTag, this.prechecks);
        this.createChecklistRow(fp, this.postCheck, this.postCheckDoneTag, this.postchecks);
        this.subscribeToTags([this.preCheckDoneTag, this.postCheckDoneTag, this.testStateTag, this.pumpTestTag, this.manualTestTag, this.manualCollectTag, this.manualEndTestTag, this.pumpStartControlTag, this.pumpSpeedControlTag, this.pumpRunningTag]);
    }

    createChecklistRow(fp: FrameParser, checklist, tag: Tag, checks: Checkbox[]) {
        let itemCount = fp.pop_u8();
        for (let i = 0; i < itemCount; ++i) {
            let template = this.shadowRoot?.getElementById('check-row') as HTMLTemplateElement;
            let checkRow = document.importNode(template.content, true);
            let title = checkRow.querySelector('#check-title') as HTMLElement;
            let check = checkRow.querySelector('#check') as Checkbox
            title.textContent = fp.pop_string();
            checklist.appendChild(checkRow);
            if (!tag.isWriteable)
                check.disabled = true;
            check.checked = tag.getValue(this);
            checks.push(check);
            check.onchange = () => this.onCheckboxChange(checks, check, tag);
        }
    }

    onCheckboxChange(checklist: Checkbox[], checkbox: Checkbox, tag: Tag) {
        if (!tag.isWriteable)					    // If we can't write the node
            checkbox.checked = false;				// Don't check the box
        else {										// We have write permission
            for (let i = 0; i < checklist.length; ++i) {	// We only write out trues here in case another client sets the box to true
                if (!checklist[i].checked) { // If a single checkbox isn't checked, don't write anything out
                    if (tag.getValue(this))
                        tag.setPendingValue(false, this);		// Write a new value to the node
                    return;
                }
            }
            tag.setPendingValue(true, this);		// Write a new value to the node
        }
    }

    updateCheckboxes(checklist: Checkbox[], tag: Tag, fCorrectState) {	// This sets the test checklist check boxes
        let fTrue = tag.getValue(this);
        for (let i = 0; i < checklist.length; ++i) {			// Make sure all the check boxes are cleared, just in case
            checklist[i].checked = fTrue;						// If the node is set, all of the check boxes should be checked
            if (!tag.isWriteable || fTrue || !fCorrectState)	// If they can't write the node, if it's already set, or if we aren't waiting on a checklist
                checklist[i].disabled = true;	// Disable all of the checkboxes
            else if (!fTrue)
                checklist[i].disabled = false;
        }
    }

    update(tag: Tag) {
        switch (tag) {
            case this.preCheckDoneTag:		// Test parameters have changed
            case this.postCheckDoneTag:
            case this.testStateTag:
                this.updateCheckboxes(this.prechecks, this.preCheckDoneTag, this.testStateTag.getValue() == 1);
                this.updateCheckboxes(this.postchecks, this.postCheckDoneTag, this.testStateTag.getValue() == 4);
            // FALLTHROUGH

            case this.pumpTestTag:
            case this.manualTestTag:
            case this.manualCollectTag:
            case this.manualEndTestTag:
            case this.pumpStartControlTag:
            case this.pumpSpeedControlTag:
            case this.pumpRunningTag:
            case this.systemStateTag:
                this.updateTestMessage();	// Update the testing message

        }
    }

    private updateTestMessage() {
        let fChangeType = false, fTestReady = false, fPreTest = false, fTesting = false, fPostTest = false, fManualTestCollect = false;
        let testState = this.testStateTag.getValue();

        // Show test status only when test is active (states 1-4)
        if (testState >= 1 && testState <= 4) {
            this.testStatus.style.display = 'block';
        } else {
            this.testStatus.style.display = 'none';
        }

        // Control accordion section availability based on test state
        this.updateAccordionAvailability(testState);

        switch (testState) {
            case 0:	// PTS_READY_TO_TEST
                if (this.pumpStartControlTag.getValue() != 3 && !this.pumpRunningTag.getValue()) {	// Pump isn't running and we can't start it
                    if (this.pumpStartControlTag.getValue())
                        this.testMessage.innerHTML = "The DPO currently can't start the pump and the pump isn't running.<br>This pump can't be tested right now.";
                    else
                        this.testMessage.innerHTML = "The DPO doesn't have start control over this pump.<br>This pump can't be tested unless it's running.";
                } else if (this.manualTestTag.getValue()) {									// We are in operator-guided test mode
                    var message = "Flip the 'Test Pump' switch to begin an operator-guided pump test, in which an operator will be responsible for varying this pump's flow (for instance, by clamping a discharge valve) and telling the DPO when to collect data points.";
                    if (this.pumpHasSpeedControlTag.getValue())
                        message += "<br>" + "This pump is a candidate for automatic testing.";
                    else if (this.pumpHasSpeedControlTag.getValue())
                        message += "<br>" + "If the DPO is given control of the pump's speed, it can be automatically tested.";
                    fTestReady = this.otherPumpsDontPreventTest();
                    if (fTestReady)
                        this.testMessage.innerHTML = message;
                } else if (!this.pumpHasSpeedControlTag.getValue()) {									// We can't control the speed of the pump
                    if (this.pumpHasSpeedControlTag.getValue())
                        this.testMessage.innerHTML = "The DPO currently can't control this pump's speed.<br>This pump can't be tested automatically right now.";
                    else
                        this.testMessage.innerHTML = "The DPO can't control this pump's speed." + "<br>" + "This pump can't be tested automatically. This pump must be tested in Operator-Guided mode.";
                } else 																			// We are in automated test mode
                    fTestReady = this.otherPumpsDontPreventTest()
                if (fTestReady)
                    this.testMessage.innerHTML = "Flip the 'Test Pump' switch to begin an automated pump test, in which the DPO will vary this pump's speed to adjust flow and automatically collect data points.";
                fChangeType = true;	// When in PTS_READY_TO_TEST, they can always flip the test type switch
                break;

            case 1:	// PTS_PRE_TEST
                if (this.pumpTestTag.getValue()) {	// Pump is set to start testing soon (or has started already)
                    this.testMessage.textContent = "The DPO will start the pump test when all the items in the pre-test checklist are acknowledged.";
                    fPreTest = true;
                    this.preTestAccordion.open();
                } else
                    this.testMessage.textContent = "Another pump is going through the pre-test checklist. Wait until that test is complete before trying to test this pump.";
                break;

            case 2:	// PTS_STARTING_TEST
                if (this.pumpTestTag.getValue()) {	// Pump is set to start testing soon (or has started already)
                    this.testMessage.textContent = "Please wait for the " + (this.manualTestTag.getValue() ? "operator-guided" : "automated") + " test to begin...";
                    fTesting = true;
                } else							// Another pump is the one testing
                    this.testMessage.textContent = "Another pump is about to begin a test. Wait until that test is complete before trying to test this pump.";
                break;

            case 3:	// PTS_TESTING
                if (this.pumpTestTag.getValue()) {	// Pump is set to start testing soon (or has started already)
                    if (!this.manualTestTag.getValue()) 	// Pump is testing automatically
                        this.testMessage.innerHTML = 'Testing this pump in automated mode.' + '<br>' + this.getSystemStateMessage(); // Get a message telling what the system is doing.
                    else {
                        this.testMessage.innerHTML = "Testing this pump in operator-guided mode." + '<br>';
                        if (this.systemStateTag.getValue() == 6) { // ST_MANUAL_TEST --> waiting on user input
                            if (this.testStepTimeTag.getValue() == 0) 	// Haven't taken a first point yet
                                this.testMessage.innerHTML += "Take the pump to its maximum flow." + '<br>';
                            else if (this.testFlows.length > 0) {
                                if (this.pumpActSpeedTag)	// If this pump has an actual speed, its speed is controllable by somebody
                                    this.testMessage.innerHTML += "Lower the pump's speed to around " + (this.testSpeed - 1).toFixed(1) + " Hz.<br>";
                                else
                                    this.testMessage.innerHTML += "Lower the pump's flow to around " + Math.max(0, Math.round(this.testFlows[this.testFlows.length - 1] - this.testFlows[0] / 10)) + " gpm.<br>";
                            }
                            this.testMessage.innerHTML += "Flip the 'Collect Point' switch when you are satisfied with the flow." + '<br>';
                            if (this.testFlows.length >= 6)
                                this.testMessage.innerHTML += "Flip the 'End Test' switch when you are satisfied with the points you have taken. This will make the DPO fit new pump curves using all the datapoints you have collected.";
                            fManualTestCollect = true;	// They can press the collect point button
                        }
                        else if (this.systemStateTag.getValue() == 7) // ST_TEST_COLLECT_STATS --> have gotten user input
                            this.testMessage.innerHTML += "Collecting a test data point. The 'Collect Point' switch will reset when enough data has been collected.";
                    }
                    fTesting = true;
                } else
                    this.testMessage.textContent = "Another pump is testing. Wait until that test is complete before trying to test this pump.";
                break;

            case 4:	// PTS_POST_TEST
                this.testMessage.textContent = "The DPO won't allow another pump test until all the items in the post-test checklist are acknowledged.";
                fPostTest = true;
                break;
        }

        // Enable controls if the user can write the node and we are ready for them
        if (!fChangeType || !this.manualTestTag.isWriteable)
            this.manTestRadio.disabled = true
        else
            this.manTestRadio.disabled = false;

        if (!fTestReady)


        this.abortButton.disabled = !fTesting || !this.pumpTestTag.isWriteable

        this.manCollectButton.disabled = !fManualTestCollect;
        this.manEndTestButton.disabled = !fTesting || !this.pumpTestTag.isWriteable || this.testFlows.length < 6;

    }

    getSystemStateMessage() {
        switch (this.systemStateTag.getValue()) {
            case 2: return "Ramping pump.";							// ST_AT_SPEED_WAIT
            case 3: return "Waiting for the system to stablize.";	// ST_QUIESCENT_WAIT
            case 4: return "Collecting a test data point.";			// ST_COLLECT_STATS
            default: return "";	// ST_UNINIT, ST_RESET, ST_STATS_READY, ST_MANUAL_TEST, ST_TEST_COLLECT_STATS. Wait for a new message we want to report on.
        }
    }

    otherPumpsDontPreventTest(): boolean {
        let pumps = this.pumpTag.tag.parent?.findByRole(Role.ROLE_PUMP) ?? [];
        for (var i = 0; i < pumps.length; ++i) {
            if (pumps[i] === this.pumpTag.tag)	// This is the pump we are trying to get info about. We've already been very thorough on him.
                continue;				// Skip ahead!
            else if (pumps[i].findChildByRole(Role.ROLE_PUMP_TEST)!.getValue()) {	// Found a pump with the testing node set
                this.testMessage.textContent = "Another pump is trying to test already. Stop " + pumps[i].getDisplayName() + " from requesting a test before testing this pump.";
                return false;	// No need to look any further
            } else if (pumps[i].findChildByRole(Role.ROLE_BOOL_RUNNING)!.getValue() && !pumps[i].findChildByRole(Role.ROLE_PUMP_START_CONTROLLED)!.getValue()) {	// Found a pump which we can't turn off
                this.testMessage.textContent = "The DPO can't stop " + pumps[i].getDisplayName() + ". This pump can't be tested until it stops.";
                return false;	// No need to look any further
            }
        }
        return true;	// Everything looks ship shape across the rest of the pumps
    }

    onSnapshotDataResponse(fp: FrameParser) {
        let start = fp.pop_u64();
        let end = fp.pop_u64();
        let count = fp.pop_u32();	// Snapshot count. Size arrays appropriately
        if (this.testStartTimeTag && start === this.testStartTimeTag.getValue()) {	// These are test snapshots
            for (let i = 0; i < count; ++i) {
                fp.skip(6);		// Skip timestamp and config
                var flow = fp.pop_f32();	// Pump flow in GPM
                var head = fp.pop_f32();	// Pump head in ft
                fp.pop_f32();				// Skip the power
                var speed = fp.pop_f32();	// Pump speed in Hz
                this.testSpeed = speed;
                var factor = speed == 0 ? 1 : this.maxSpeed / speed;	// Use the affinity laws to calculate the full speed points
                this.testFlows[i] = flow * this.flowConversion * factor;
                this.testHeads[i] = head * this.headConversion * factor * factor;
            }
        } else	// Not test snapshots and not a request for pump operation. Must be something we wanted, but kept going
            fp.skip(fp.size());
    }

    toggleArrow(image: HTMLImageElement, accordion: Accordion) {
        image.src = ArrowIcon;
        if (accordion.isOpen) {
            image.style.transform = 'rotate(90deg)';
        } else {
            image.style.transform = 'rotate(0deg)';
        }
    }

    private updateAccordionAvailability(testState: number) {
        // Reset all sections to disabled first
        this.setAccordionSectionEnabled(this.preTestSection, false);
        this.setAccordionSectionEnabled(this.testControlsSection, false);
        this.setAccordionSectionEnabled(this.postTestSection, false);

        // Start section is always enabled
        this.setAccordionSectionEnabled(this.startSection, true);

        switch (testState) {
            case 0: // READY_TO_TEST
                // Only start section enabled (already set above)
                break;

            case 1: // PRE_TEST
            case 2: // STARTING_TEST
            case 3: // TESTING
            case 4: // POST_TEST
                // Keep pretest checklist available once test has started
                this.setAccordionSectionEnabled(this.preTestSection, true);

                if (testState === 3) { // TESTING
                    // Only enable test controls for manual tests
                    if (this.manualTestTag.getValue()) {
                        this.setAccordionSectionEnabled(this.testControlsSection, true);
                    }
                }

                if (testState === 4) { // POST_TEST
                    this.setAccordionSectionEnabled(this.postTestSection, true);
                }
                break;
        }
    }

    private setAccordionSectionEnabled(section: HTMLElement, enabled: boolean) {
        if (enabled) {
            section.classList.remove('disabled');
        } else {
            section.classList.add('disabled');
        }
    }
}